import { evaluate as evaluateTree } from './Evaluator';
import { extractReferencesFromTree, parse } from './Parser';
import type { ParseNode } from './ParseTree';

/**
 * Expression class that wraps a parsed calculator expression
 * Provides convenient access to parse tree, variables, functions, and evaluation
 */
export class Expression {
  private tree: ParseNode;
  private cachedParameters?: string[];
  private cachedFunctions?: string[];

  constructor(input: string) {
    this.tree = parse(input);
  }

  /**
   * Get the parse tree (AST) for this expression
   */
  get parseTree(): ParseNode {
    return this.tree;
  }

  /**
   * Get sorted list of all parameter names used in this expression
   */
  get parameters(): string[] {
    if (!this.cachedParameters) {
      const refs = extractReferencesFromTree(this.tree);
      this.cachedParameters = refs.parameters;
    }
    return this.cachedParameters;
  }

  /**
   * Get sorted list of all function names used in this expression
   */
  get functions(): string[] {
    if (!this.cachedFunctions) {
      const refs = extractReferencesFromTree(this.tree);
      this.cachedFunctions = refs.functions;
    }
    return this.cachedFunctions;
  }

  /**
   * Evaluate the expression with provided parameter values and function implementations
   *
   * @param parameters - Map of parameter names to their numeric values (can include undefined)
   * @param functions - Map of function names to JavaScript functions
   *                    Functions receive evaluated arguments and can return number | undefined
   * @returns The numeric result of evaluating the expression, or undefined if evaluation failed
   *
   * @example
   * const expr = new Expression('max(a, b) + c * 2')
   * const result = expr.evaluate(
   *   { a: 5, b: 10, c: 3 },
   *   { max: (a, b) => a === undefined || b === undefined ? undefined : Math.max(a, b) }
   * )
   * // result = 16
   */
  evaluate(
    parameters: Record<string, number | undefined>,
    functions: Record<string, (...args: (number | undefined)[]) => number | undefined>,
  ): number | undefined {
    return evaluateTree(this.tree, parameters, functions);
  }
}
